import type {
  BrandfetchColor,
  BrandfetchFont,
  BrandfetchLogo,
  BrandfetchResponse,
} from "../types";

/**
 * Slim snapshot of a Brandfetch response, persisted on the story so the
 * design-system dialog can rehydrate the brand result card on next open
 * without re-hitting the Brandfetch API.
 *
 * Only includes the fields the result card actually renders. The shape
 * mirrors `BrandfetchResponse` so existing components can consume it
 * without changes (it's structurally compatible — `BrandResultCard` only
 * touches the fields below).
 */
export type BrandImportSnapshot = {
  name: string;
  domain: string;
  description?: string;
  colors: BrandfetchColor[];
  fonts: BrandfetchFont[];
  logos: BrandfetchLogo[];
};

/**
 * Build the persisted snapshot from a fresh Brandfetch response. Strips
 * unused fields (longDescription, images, links) to keep the story payload
 * small. Returns null if the response is missing the bare-minimum brand
 * identity (so we never persist a useless empty placeholder).
 */
export const toBrandImportSnapshot = (
  data: BrandfetchResponse,
): BrandImportSnapshot | null => {
  if (!data?.name && !data?.domain) return null;
  return {
    name: data.name ?? "",
    domain: data.domain ?? "",
    description: data.description,
    colors: data.colors ?? [],
    fonts: data.fonts ?? [],
    logos: data.logos ?? [],
  };
};

/**
 * Type guard / lightweight validator for snapshots read back off the story
 * settings — guards against malformed persisted data.
 */
export const isBrandImportSnapshot = (
  value: unknown,
): value is BrandImportSnapshot => {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.name === "string" &&
    Array.isArray(v.colors) &&
    Array.isArray(v.fonts) &&
    Array.isArray(v.logos)
  );
};
